import {
  StrictMode,
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { router } from "./router.js";
import { api, type AppConfig } from "./lib/api.js";
import { ConfigProvider, isClerkMode, productName } from "./lib/config.js";
import { Splash } from "./auth/clerk-lazy.js";
// Brand typeface (self-hosted variable font — the brand's web typeface; there
// is no monospace in the brand, so everything renders in Manrope).
import "@fontsource-variable/manrope/index.css";
import "./styles.css";

// Clerk lives in its own lazily-loaded chunk so enterprise/dev never fetch
// the ~150KB Clerk bundle (see auth/ClerkApp.tsx).
const ClerkApp = lazy(() => import("./auth/ClerkApp.js"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

function Shell({ config }: { config: AppConfig }) {
  // index.html defaults the title to the b2c product ("makerdoc.ai"); the
  // enterprise edition is "MakerX Multiplayer". Set it once config is known.
  // Pages with a name of their own (the deck editor) prefix it via
  // useDocumentTitle and restore this on unmount.
  useEffect(() => {
    document.title = productName(config);
  }, [config]);

  const app = <RouterProvider router={router} />;

  const tree =
    isClerkMode(config) && config.clerkPublishableKey ? (
      <Suspense fallback={Splash}>
        <ClerkApp publishableKey={config.clerkPublishableKey}>{app}</ClerkApp>
      </Suspense>
    ) : (
      app
    );

  return (
    <ConfigProvider config={config}>
      <QueryClientProvider client={queryClient}>{tree}</QueryClientProvider>
    </ConfigProvider>
  );
}

/**
 * Boot fetches runtime config before mounting the app. Unlike a top-level
 * await (which leaves a blank page until the request resolves and a white
 * screen of death if it fails), this paints a branded splash immediately
 * and shows an actionable Retry on failure — important for Container Apps
 * scale-from-zero cold starts.
 */
function Boot() {
  const requestRef = useRef<AbortController | null>(null);
  const [state, setState] = useState<{
    status: "loading" | "error" | "ready";
    config?: AppConfig;
  }>({ status: "loading" });

  const load = useCallback(() => {
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    setState({ status: "loading" });
    api
      .config({ signal: controller.signal })
      .then((config) => {
        if (!controller.signal.aborted) setState({ status: "ready", config });
      })
      .catch(() => {
        if (!controller.signal.aborted) setState({ status: "error" });
      });
  }, []);

  useEffect(() => {
    load();
    return () => requestRef.current?.abort();
  }, [load]);

  if (state.status === "ready" && state.config) {
    return <Shell config={state.config} />;
  }
  if (state.status === "error") {
    // Config (which carries the edition) failed to load, so we can't use
    // isClerkMode here. Derive the product name from the host instead: the
    // enterprise app is served from a makerx.* domain; everything else (b2c
    // app.makerdoc.ai, and localhost) is the unbranded makerdoc.ai product.
    // Keeps "MakerX" off the b2c error screen.
    const productName = window.location.hostname.includes("makerx")
      ? "MakerX Multiplayer"
      : "makerdoc.ai";
    return (
      <div className="min-h-screen flex items-center justify-center px-6 text-center">
        <div className="space-y-4">
          <p className="mx-eyebrow text-white/45">{productName}</p>
          <p className="text-white/70 text-sm">
            Couldn't reach the service. It may be starting up.
          </p>
          <button
            type="button"
            onClick={load}
            className="px-4 py-2 rounded bg-[var(--color-mx-electric)] text-black text-sm font-medium"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }
  return Splash;
}

const root = document.getElementById("root");
if (!root) throw new Error("#root element not found");

createRoot(root).render(
  <StrictMode>
    <Boot />
  </StrictMode>,
);
